import { PostSource, PostStatus } from './constants';
import type { BusinessLiteDto, BusinessModel } from '../business/types';

export enum ClickoutTarget {
    BUTTON = 'button',
    GRID = 'grid',
}

type ClickOutDto = {
    url: string;
    url_display: string;
    target: ClickoutTarget;
    text: string;
};

export enum ReactionState {
    LIKE = 'like',
    DISLIKE = 'dislike',
    NONE = 'none',
}

export type Content<T extends 'dto' | 'model' = 'dto'> =
    | {
          id: string;
          type: 'image';
          media: Inspire.Image[];
      }
    | {
          id: string;
          type: 'video';
          media: T extends 'dto' ? Inspire.VideoDto[] : Inspire.Video[];
      };

export interface PriceDto {
    currency: string;
    integer_part: number;
    fractional_part?: number;
    discount_percent?: number;
}

export interface FintechDiscountItemDto {
    discount_price: number;
    discount_percent: number;
    max_discount_amount?: number;
    max_discount_percent?: number;
}

export interface FintechDiscountDto {
    ya_pay?: FintechDiscountItemDto;
    ya_split?: FintechDiscountItemDto;
}

export interface CheckoutDto {
    url?: string;
}

export interface ProductItemDto {
    id: string;
    url: string;
    original_url: string;
    title: string;
    content: Content[];
    price_info: PriceDto;
    old_price_info?: PriceDto;
    monetization_enabled?: boolean;
    is_unavailable?: boolean;
    fintech_discount?: FintechDiscountDto;
    checkout?: CheckoutDto;
}

export interface ProductsDto {
    items: ProductItemDto[];
    button_title: string;
    business_button_title: string;
    description: string;
    title: string;
}

export interface PriceModel {
    currency: string;
    integerPart: number;
    fractionalPart?: number;
    discountPercent?: number;
}

export interface FintechDiscountItemModel {
    discountPrice: number;
    discountPercent: number;
    maxDiscountAmount?: number;
    maxDiscountPercent?: number;
}

export interface FintechDiscountModel {
    yaPay?: FintechDiscountItemModel;
    yaSplit?: FintechDiscountItemModel;
}

export interface CheckoutModel {
    url?: string;
}

export type ProductItemModel = Omit<
    ProductItemDto,
    | 'price_info'
    | 'old_price_info'
    | 'content'
    | 'monetization_enabled'
    | 'original_url'
    | 'fintech_discount'
    | 'checkout'
> & {
    content: Content<'model'>[];
    originalUrl: string;
    priceInfo: PriceModel;
    oldPriceInfo: PriceModel;
    monetizationEnabled?: boolean;
    isUnavailable?: boolean;
    fintechDiscount?: FintechDiscountModel;
    checkout?: CheckoutModel;
};

export type ProductsModel = Omit<
    ProductsDto,
    'items' | 'button_title' | 'business_button_title'
> & {
    items: ProductItemModel[];
    buttonTitle: string;
    businessButtonTitle: string;
};

export interface OwnersDataDto {
    author: {
        id: string;
        display_name: string;
        avatar_url: Inspire.Image[];
        share_link: string;
    };
    shop: {
        display_url: string;
    };
    is_owner: boolean;
}

export interface PostCollectionDto {
    id: string;
    title: string;
    number_of_posts: number;
    button_text: string;
    is_saved: boolean;
}

export interface PostDto {
    id: string;
    text: string;
    views: number;
    reaction_state: ReactionState;
    saved: boolean;
    tags: string[];
    status: PostStatus;
    declined_reason?: DeclinedReason;
    post_url: string;
    data_source: PostSource;
    business?: BusinessLiteDto;
    clickouts: ClickOutDto[];
    content: Content[];
    created_at: string;
    dj_request_id?: string;
    log_context?: string;
    products_info?: ProductsDto;
    ads_data?: {
        show_url?: string;
    };
    post_type?: 'post' | 'product' | 'collection';
    owners_data?: OwnersDataDto;
    collection_data: PostCollectionDto;
    /** Состояние тумблера продвижения поста. */
    enabled_default_boosting?: boolean;
    stats?: PostStatDto;
    public_stats?: {
        shows?: number;
        likes?: number;
    };
    monetization_enabled?: boolean;
    is_ai_content?: boolean;
}

type ClickOutModel = {
    url: ClickOutDto['url'];
    urlDisplay: ClickOutDto['url_display'];
    target: ClickOutDto['target'];
    text: ClickOutDto['text'];
};

export interface OwnersDataModel {
    author: {
        id: string;
        displayName: string;
        avatarUrl: Inspire.Image[];
        shareLink: string;
    };
    shop: {
        displayUrl: string;
    };
    isOwner: boolean;
}

export interface PostCollectionModel {
    id: PostCollectionDto['id'];
    title: PostCollectionDto['title'];
    numberOfPosts: PostCollectionDto['number_of_posts'];
    buttonText: PostCollectionDto['button_text'];
    isSaved: PostCollectionDto['is_saved'];
}

export enum DeclinedReason {
    NEED_PRODUCT = 'need_product',
}

export interface PostModel
    extends Omit<
        PostDto,
        | 'business'
        | 'categories'
        | 'content'
        | 'created_at'
        | 'clickouts'
        | 'data_source'
        | 'post_url'
        | 'reaction_state'
        | 'products'
        | 'dj_request_id'
        | 'post_type'
        | 'owners_data'
        | 'products_info'
        | 'collection_data'
        | 'declined_reason'
        | 'enabled_default_boosting'
        | 'stats'
        | 'monetization_enabled'
    > {
    createdAt: string;
    url: string;
    source: PostSource;
    shareUrl: string;
    djRequestId?: string;
    logContext?: string;
    clickouts: ClickOutModel[];
    business?: BusinessModel;
    reactionState: ReactionState;
    content: Content<'model'>[];
    products: ProductsModel;
    postType?: 'post' | 'product' | 'collection';
    ownersData?: OwnersDataModel;
    adsData?: {
        showUrl?: string;
    };
    collectionData?: PostCollectionModel;
    declinedReason?: DeclinedReason;
    /** Состояние тумблера продвижения поста. */
    enabledDefaultBoosting: boolean;
    stats?: PostStatModel;
    likes: number;
    publicStats?: {
        shows?: number;
        likes?: number;
    };
    monetizationEnabled?: boolean;
    isAIContent?: boolean;
}

export interface PostDataModel extends Omit<PostModel, 'business' | 'categories'> {
    business?: BusinessModel['id'];
}

export interface PostPersistenceDto {
    business_id?: string;
    text: string;
    media?: Array<{
        id: string;
        url: string;
        type: 'image' | 'video';
    }>;
    status?: PostStatus;
    decline_reason?: string;
    data_source: PostSource;
    clickouts: ClickOutDto[];
    product_ids: string[];
    need_cpa_registration?: boolean;
    is_ai_content?: boolean;
}

export interface PostPersistenceModel {
    id?: string;
    text: string;
    businessId?: string;
    status?: PostStatus;
    declinedReason?: DeclinedReason;
    source?: PostSource;
    media?: Array<{
        id: string;
        url: string;
        type: 'image' | 'video';
    }>;
    clickouts: ClickOutModel[];
    productIds: string[];
    needCpaRegistration?: boolean;
    isAIContent?: boolean;
}

export enum ReportReasons {
    OTHER = '0',
    VIOLENCE = '1',
    TRAGIC_CONTENT = '2',
    OFFENSIVE_REMARKS = '3',
    ADULT_CONTENT = '4',
    FRAUD = '5',
    SPAM_AND_ILLEGAL_CONTENT = '6',
    SELF_MEDICATION = '7',
    VIOLATION_OF_LAW = '8',
    CONTENT_INCONSISTENCY = '9',
    INTELLECTUAL_PROPERTY_VIOLATION = '10',
}

export interface PostStatItem {
    value: number;
    display: string;
    diffValue: number;
    diffDisplay: string;
}

export interface PostStatModel {
    coverage: {
        total: PostStatItem;
        subscribers: PostStatItem;
        notsubscribers: PostStatItem;
        boosted?: PostStatItem;
        organic?: PostStatItem;
    };
    involved: {
        total: PostStatItem;
        subscribers: PostStatItem;
        notsubscribers: PostStatItem;
        boosted?: PostStatItem;
        organic?: PostStatItem;
    };
    shows: {
        total: PostStatItem;
        subscribers: PostStatItem;
        notsubscribers: PostStatItem;
        boosted?: PostStatItem;
        organic?: PostStatItem;
    };
    showsInGrid: {
        total: PostStatItem;
        subscribers: PostStatItem;
        notsubscribers: PostStatItem;
        boosted?: PostStatItem;
        organic?: PostStatItem;
    };
    subscribers: {
        total: PostStatItem;
        subscribed: PostStatItem;
        unsubscribed: PostStatItem;
    };
    interactions: {
        total: PostStatItem;
        clicks: PostStatItem;
        likes: PostStatItem;
        shares: PostStatItem;
        saves: PostStatItem;
    };
    clicks?: {
        total: PostStatItem;
        subscribers: PostStatItem;
        notsubscribers: PostStatItem;
        boosted?: PostStatItem;
        organic?: PostStatItem;
    };
}

export interface PostStatItemDto {
    value: number;
    display: string;
    diff_value: number;
    diff_display: string;
}

export interface PostStatDto {
    coverage: {
        total: PostStatItemDto;
        subscribers: PostStatItemDto;
        notsubscribers: PostStatItemDto;
        boosted?: PostStatItemDto;
        organic?: PostStatItemDto;
    };
    involved: {
        total: PostStatItemDto;
        subscribers: PostStatItemDto;
        notsubscribers: PostStatItemDto;
        boosted?: PostStatItemDto;
        organic?: PostStatItemDto;
    };
    shows: {
        total: PostStatItemDto;
        subscribers: PostStatItemDto;
        notsubscribers: PostStatItemDto;
        boosted?: PostStatItemDto;
        organic?: PostStatItemDto;
    };
    shows_in_grid: {
        total: PostStatItemDto;
        subscribers: PostStatItemDto;
        notsubscribers: PostStatItemDto;
        boosted?: PostStatItemDto;
        organic?: PostStatItemDto;
    };
    subscribers: {
        total: PostStatItemDto;
        subscribed: PostStatItemDto;
        unsubscribed: PostStatItemDto;
    };
    interactions: {
        total: PostStatItemDto;
        clicks: PostStatItemDto;
        likes: PostStatItemDto;
        shares: PostStatItemDto;
        saves: PostStatItemDto;
    };
    clicks?: {
        total: PostStatItemDto;
        subscribers: PostStatItemDto;
        notsubscribers: PostStatItemDto;
        boosted?: PostStatItemDto;
        organic?: PostStatItemDto;
    };
}

export interface AdCard {
    isAd: true;
    id: string;
}
